import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  FiZap, FiCheckCircle, FiExternalLink, FiCopy, FiFileText,
  FiClock, FiHash, FiTag, FiAlertCircle, FiLink, FiXCircle,
  FiLoader, FiEdit3, FiInfo,
} from 'react-icons/fi';
import './views.css';

const fmt = (iso) => {
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
};

// Permissive sanitize for live typing — keeps trailing hyphens so users can
// naturally type "foo-" then "bar" to get "foo-bar". Only the visual feedback
// runs through this; the final form goes through slugifyFinal before submit.
const slugifyLive = (s) =>
  String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-');

// Strict — matches the backend exactly. Used for the availability check and
// the actual POST body, never for what the user sees in the field.
const slugifyFinal = (s) =>
  slugifyLive(s)
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '');

const CreatePageView = () => {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState('panchangam');
  const [topic, setTopic] = useState('');
  const [slug, setSlug] = useState('');
  const [slugTouched, setSlugTouched] = useState(false);
  const [slugStatus, setSlugStatus] = useState({ state: 'idle' }); // idle | checking | available | taken | invalid
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const checkTimer = useRef(null);

  useEffect(() => {
    fetch('/api/pages/categories')
      .then((r) => r.json())
      .then((j) => { if (j.success) setCategories(j.data); })
      .catch(() => {
        setCategories([
          { id: 'panchangam',  label: 'Panchangam' },
          { id: 'horoscope',   label: 'Horoscope' },
          { id: 'matchmaking', label: 'Match Making' },
          { id: 'astrology',   label: 'Generic Astrology Pages' },
        ]);
      });
  }, []);

  // The strict form used for checking availability and submitting.
  // Differs from `slug` (raw input) when the user is mid-edit with a
  // trailing hyphen or repeated hyphens.
  const finalSlug = slugifyFinal(slug);
  const slugDiffersFromFinal = slug !== finalSlug && slug.length > 0;

  // Auto-derive slug from topic, but only while the user hasn't manually edited the slug.
  useEffect(() => {
    if (!slugTouched) setSlug(slugifyFinal(topic));
  }, [topic, slugTouched]);

  // Debounced availability check — always sent in the strict form so the
  // backend isn't asked to validate "foo-" while the user is still typing.
  useEffect(() => {
    clearTimeout(checkTimer.current);
    if (!finalSlug) {
      setSlugStatus({ state: 'idle' });
      return;
    }
    setSlugStatus({ state: 'checking' });
    checkTimer.current = setTimeout(async () => {
      try {
        const res = await fetch(`/api/pages/check-slug/${encodeURIComponent(finalSlug)}`);
        const json = await res.json();
        if (!json.success) {
          setSlugStatus({ state: 'invalid', message: 'Could not check slug.' });
          return;
        }
        if (json.reason === 'invalid') {
          setSlugStatus({ state: 'invalid', message: json.message });
          return;
        }
        if (json.available) {
          setSlugStatus({ state: 'available' });
        } else {
          setSlugStatus({ state: 'taken', suggestion: json.suggestion });
        }
      } catch {
        setSlugStatus({ state: 'invalid', message: 'API unreachable.' });
      }
    }, 350);
    return () => clearTimeout(checkTimer.current);
  }, [finalSlug]);

  const onTopicChange = (e) => {
    setTopic(e.target.value);
    setError('');
  };

  const onSlugChange = (e) => {
    setSlugTouched(true);
    setSlug(slugifyLive(e.target.value));
    setError('');
  };

  const applySuggestion = () => {
    if (slugStatus.suggestion) {
      setSlug(slugStatus.suggestion);
      setSlugTouched(true);
    }
  };

  const resetSlug = () => {
    setSlugTouched(false);
    setSlug(slugifyFinal(topic));
  };

  const canSubmit =
    !!topic.trim() &&
    !!finalSlug &&
    slugStatus.state === 'available' &&
    !generating;

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setResult(null);
    if (!topic.trim()) return setError('Please enter a topic / SEO Page Name.');
    if (slugStatus.state === 'taken') return setError('Slug is already in use. Pick a different one.');
    if (slugStatus.state === 'invalid') return setError('Slug is invalid. Use letters, numbers and hyphens.');
    if (slugStatus.state === 'checking') return setError('Still checking slug availability — please wait.');

    setGenerating(true);
    try {
      const res = await fetch('/api/pages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category, topic: topic.trim(), slug: finalSlug }),
      });
      const json = await res.json();
      if (!json.success) {
        if (res.status === 409) {
          setSlugStatus({ state: 'taken' });
          throw new Error(`Slug "${json.slug || finalSlug}" was just taken. Choose another.`);
        }
        throw new Error(json.error || 'Generation failed');
      }
      setResult(json.data);
      setTopic('');
      setSlug('');
      setSlugTouched(false);
      setSlugStatus({ state: 'idle' });
    } catch (err) {
      setError(err.message || 'Could not reach the page generator API.');
    } finally {
      setGenerating(false);
    }
  };

  const copy = (text) => navigator.clipboard.writeText(text);

  return (
    <div className="vw">
      <motion.div
        className="vw-card vw-form-card"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <div className="vw-card-head">
          <div className="vw-icon-tile"><FiZap /></div>
          <div>
            <h2>Programmatic SEO Page Generator</h2>
            <p>Generate a fully optimised 1500-word landing page in seconds — title, meta, intro, sections, FAQs, schema, related links, CTA.</p>
          </div>
        </div>

        <form onSubmit={submit} className="vw-form">
          <div className="vw-row">
            <label className="vw-field">
              <span className="vw-label">Category</span>
              <div className="vw-select-wrap">
                <FiTag className="vw-field-icon" />
                <select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>{c.label}</option>
                  ))}
                </select>
              </div>
            </label>

            <label className="vw-field vw-field-grow">
              <span className="vw-label">Topic / SEO Page Name</span>
              <div className="vw-select-wrap">
                <FiHash className="vw-field-icon" />
                <input
                  type="text"
                  value={topic}
                  onChange={onTopicChange}
                  placeholder="e.g. Rahu Kalam Today Chennai"
                  maxLength={120}
                />
              </div>
            </label>
          </div>

          <div className="vw-field vw-field-grow">
            <span className="vw-label vw-label-row">
              <span>
                URL Slug{' '}
                <span className={`vw-slug-mode vw-slug-mode-${slugTouched ? 'custom' : 'auto'}`}>
                  {slugTouched ? <><FiEdit3 /> Custom</> : 'Auto from topic'}
                </span>
              </span>
              {slugTouched ? (
                <button type="button" className="vw-link-btn" onClick={resetSlug}>
                  Reset to auto
                </button>
              ) : (
                <span className="vw-slug-hint-inline">
                  Click below to enter a custom slug
                </span>
              )}
            </span>

            <div className={`vw-select-wrap vw-slug-input vw-slug-${slugStatus.state}`}>
              <FiLink className="vw-field-icon" />
              <span className="vw-slug-prefix">/blog/</span>
              <input
                type="text"
                value={slug}
                onChange={onSlugChange}
                onFocus={(e) => {
                  // If still in auto mode, select all so a single keystroke replaces it.
                  if (!slugTouched && e.target.value) e.target.select();
                }}
                placeholder="type-your-custom-slug-here"
                spellCheck={false}
                autoComplete="off"
              />
              <SlugStatusIcon state={slugStatus.state} />
            </div>

            <SlugStatusMessage status={slugStatus} onApplySuggestion={applySuggestion} />

            {slugDiffersFromFinal && (
              <div className="vw-slug-msg vw-slug-normalize">
                Will be saved as <code>/blog/{finalSlug}</code>
              </div>
            )}

            <p className="vw-slug-tip">
              <FiInfo /> Lowercase letters, numbers and hyphens only. Spaces become hyphens; uppercase becomes lowercase automatically.
            </p>
          </div>

          {error && (
            <div className="vw-error">
              <FiAlertCircle /> {error}
            </div>
          )}

          <button type="submit" className="vw-btn vw-btn-primary" disabled={!canSubmit}>
            {generating ? (
              <><span className="vw-spinner" /> Generating page…</>
            ) : (
              <><FiZap /> Generate Page</>
            )}
          </button>

          <div className="vw-presets">
            <span>Try:</span>
            {[
              'Today Panchangam Chennai',
              'Rahu Kalam Today Bangalore',
              'Aries Daily Horoscope 2026',
              'Manglik Dosha Remedies',
              'Kundli Matching For Marriage',
            ].map((p) => (
              <button
                type="button"
                key={p}
                className="vw-preset"
                onClick={() => { setTopic(p); setSlugTouched(false); }}
              >
                {p}
              </button>
            ))}
          </div>
        </form>
      </motion.div>

      <AnimatePresence>
        {result && (
          <motion.div
            className="vw-card vw-result-card"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ type: 'spring', damping: 22, stiffness: 240 }}
          >
            <div className="vw-result-head">
              <div className="vw-success">
                <FiCheckCircle /> Page generated successfully
              </div>
              <a
                className="vw-btn vw-btn-ghost vw-btn-sm"
                href={`/blog/${result.slug}`}
                target="_blank"
                rel="noreferrer"
              >
                <FiExternalLink /> View Live Page
              </a>
            </div>

            <div className="vw-result-grid">
              <div className="vw-result-meta">
                <div className="vw-meta-row">
                  <span className="vw-meta-label">SEO Title</span>
                  <div className="vw-meta-value">
                    <span>{result.title}</span>
                    <button onClick={() => copy(result.title)} title="Copy"><FiCopy /></button>
                  </div>
                </div>

                <div className="vw-meta-row">
                  <span className="vw-meta-label">Meta Description</span>
                  <div className="vw-meta-value">
                    <span>{result.metaDescription}</span>
                    <button onClick={() => copy(result.metaDescription)} title="Copy"><FiCopy /></button>
                  </div>
                </div>

                <div className="vw-meta-row">
                  <span className="vw-meta-label">Canonical URL</span>
                  <div className="vw-meta-value">
                    <code>{result.canonicalUrl}</code>
                    <button onClick={() => copy(result.canonicalUrl)} title="Copy"><FiCopy /></button>
                  </div>
                </div>

                <div className="vw-meta-row">
                  <span className="vw-meta-label">H1 Heading</span>
                  <div className="vw-meta-value"><strong>{result.h1}</strong></div>
                </div>

                <div className="vw-meta-row">
                  <span className="vw-meta-label">Slug</span>
                  <div className="vw-meta-value"><code>/blog/{result.slug}</code></div>
                </div>

                <div className="vw-meta-row">
                  <span className="vw-meta-label">
                    Secondary Keywords ({result.secondaryKeywords?.length || 0})
                  </span>
                  <div className="vw-keyword-chips">
                    {(result.secondaryKeywords || []).map((kw, i) => (
                      <span key={i} className="vw-kw-chip">{kw}</span>
                    ))}
                    <button
                      className="vw-kw-copy"
                      onClick={() => copy((result.secondaryKeywords || []).join(', '))}
                      title="Copy all"
                    >
                      <FiCopy /> Copy all
                    </button>
                  </div>
                </div>
              </div>

              <div className="vw-result-stats">
                <div className="vw-stat-pill">
                  <FiFileText /> <strong>{result.wordCount.toLocaleString()}</strong> words
                </div>
                <div className="vw-stat-pill">
                  <FiHash /> <strong>{result.faqs.length}</strong> FAQs
                </div>
                <div className="vw-stat-pill">
                  <FiTag /> {result.categoryLabel}
                </div>
                <div className="vw-stat-pill">
                  <FiClock /> {fmt(result.createdAt)}
                </div>
                <div className="vw-stat-pill ok">
                  <FiCheckCircle /> JSON-LD x{result.jsonLd?.length || 0}
                </div>
                <div className="vw-stat-pill ok">
                  <FiCheckCircle /> {result.status}
                </div>
              </div>
            </div>

            <div className="vw-preview">
              <div className="vw-preview-head">Page Preview</div>

              <h1 className="vw-preview-h1">{result.h1}</h1>
              <p className="vw-preview-intro">{result.intro}</p>

              {result.sections.slice(0, 2).map((s, i) => (
                <div key={i} className="vw-preview-section">
                  <h3>{s.heading}</h3>
                  <p>{s.body.slice(0, 320)}…</p>
                </div>
              ))}

              <div className="vw-preview-fade" />
              <div className="vw-preview-foot">
                Showing first 2 of {result.sections.length} sections.{' '}
                <a href={`/blog/${result.slug}`} target="_blank" rel="noreferrer">
                  View full page →
                </a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const SlugStatusIcon = ({ state }) => {
  if (state === 'checking')  return <FiLoader className="vw-slug-icon spin" />;
  if (state === 'available') return <FiCheckCircle className="vw-slug-icon ok" />;
  if (state === 'taken')     return <FiXCircle className="vw-slug-icon bad" />;
  if (state === 'invalid')   return <FiAlertCircle className="vw-slug-icon bad" />;
  return null;
};

const SlugStatusMessage = ({ status, onApplySuggestion }) => {
  if (status.state === 'checking') {
    return <div className="vw-slug-msg checking">Checking availability…</div>;
  }
  if (status.state === 'available') {
    return <div className="vw-slug-msg ok">Available — this URL is yours.</div>;
  }
  if (status.state === 'taken') {
    return (
      <div className="vw-slug-msg bad">
        Slug already exists.
        {status.suggestion && (
          <>
            {' '}Try{' '}
            <button type="button" className="vw-link-btn vw-link-strong" onClick={onApplySuggestion}>
              {status.suggestion}
            </button>
          </>
        )}
      </div>
    );
  }
  if (status.state === 'invalid') {
    return <div className="vw-slug-msg bad">{status.message || 'Invalid slug.'}</div>;
  }
  return null;
};

export default CreatePageView;
